import { IGruposRentabilidadeResponse } from '../../../core/settings/grupos-rentabilidade/interfaces/grupos-rentabilidade-response.interface';
import { IDNAExpectativas } from '../../dnas/interfaces/dna-expectativas.interface';
import { TIPO_ESTRATEGICO_PLM_LABELS } from '../../plm/enums/tipo-estrategico-plm.enum';
import { IItemDNA } from '../interfaces/item-dna.interface';
import { IPLM } from '../interfaces/plm.interface';
import { estaPreenchido } from './heranca-dna.util';

export interface ILinhaHeranca {
  numero: number;
  label: string;
  valor: string;
  valorHerdado: string | null;
  projecao: string | null;
  projecaoMedindo?: boolean;
  mutacao: boolean;
}

interface IContexto {
  dna: IItemDNA;
  item: Partial<IPLM>;
  expectativas: IDNAExpectativas | null;
  gruposRentabilidade: IGruposRentabilidadeResponse[];
  idGrupoRentabilidadeItem?: number | null;
  populacaoDoItem?: IPopulacaoMedida | null;
}

export interface IPopulacaoMedida {
  total: number | null;
  mes: number | null;
  ano: number | null;
  medindo?: boolean;
}

const SEM_VALOR = '—';

const formatarNumero = (valor: number, casas: number): string =>
  valor.toLocaleString('pt-BR', { maximumFractionDigits: casas });

const formatarPercentual = (valor: number): string => `${formatarNumero(valor, 2)}%`;

const formatarCurva = (taxas: number[]): string =>
  `${taxas.map((taxa) => formatarNumero(taxa, 2)).join(' · ')}%`;

const nomeDoGrupo = (grupos: IGruposRentabilidadeResponse[], id: number): string =>
  grupos.find((grupo) => grupo.id === id)?.nome ?? SEM_VALOR;

const contarPublicos = (total: number): string =>
  total ? `${total} ${total === 1 ? 'público' : 'públicos'}` : SEM_VALOR;

const projecaoPopulacao = (expectativas: IDNAExpectativas | null): string | null => {
  if (!expectativas) {
    return null;
  }
  const competencia = `${String(expectativas.mes).padStart(2, '0')}/${expectativas.ano}`;
  return `${formatarNumero(
    expectativas.populacaoElegivel,
    0
  )} clientes · carteira de ${competencia}`;
};

const projecaoMedida = (populacao: IPopulacaoMedida | null | undefined): string | null => {
  if (!populacao || populacao.total === null) {
    return populacao?.medindo ? 'medindo a população' : null;
  }
  const clientes = `${formatarNumero(populacao.total, 0)} clientes`;
  if (populacao.mes === null || populacao.ano === null) {
    return clientes;
  }
  const competencia = `${String(populacao.mes).padStart(2, '0')}/${populacao.ano}`;
  return `${clientes} · carteira de ${competencia}`;
};

const projecaoClientesEsperados = (expectativas: IDNAExpectativas | null): string | null =>
  expectativas
    ? `${formatarNumero(expectativas.clientesEsperados, 0)} clientes esperados`
    : null;

const linhaSimples = (
  numero: number,
  label: string,
  config: {
    doItem: number | undefined;
    doDna: number;
    formatar: (valor: number) => string;
    projecao?: string | null;
  }
): ILinhaHeranca => {
  const informado = estaPreenchido(config.doItem);
  const mutacao = informado && config.doItem !== config.doDna;
  return {
    numero,
    label,
    valor: informado ? config.formatar(config.doItem) : config.formatar(config.doDna),
    valorHerdado: mutacao ? config.formatar(config.doDna) : null,
    projecao: config.projecao ?? null,
    mutacao,
  };
};

const linhaCurva = ({ dna, item }: IContexto): ILinhaHeranca => {
  const doDna = [
    dna.taxaMaturacaoNascimento,
    dna.taxaMaturacaoCrescimento,
    dna.taxaMaturacaoFortalecimento,
    dna.taxaMaturacaoMaturidade,
  ];
  const doItem = [
    item.taxaMaturacaoNascimentoPLM,
    item.taxaMaturacaoCrescimentoPLM,
    item.taxaMaturacaoFortalecimentoPLM,
    item.taxaMaturacaoMaturidadePLM,
  ];

  const vigente = doDna.map((taxa, indice) =>
    estaPreenchido(doItem[indice]) ? doItem[indice] : taxa
  );
  const mutacao = vigente.some((taxa, indice) => taxa !== doDna[indice]);

  const pesoMaduro = estaPreenchido(item.pesoMedioMaturacaoPLM)
    ? item.pesoMedioMaturacaoPLM
    : dna.pesoMedioMaturacao;

  return {
    numero: 5,
    label: 'Curva de Maturação',
    valor: formatarCurva(vigente),
    valorHerdado: mutacao ? formatarCurva(doDna) : null,
    projecao: estaPreenchido(pesoMaduro)
      ? `${vigente.map((taxa) => formatarNumero((pesoMaduro * taxa) / 100, 2)).join(' · ')} KG`
      : null,
    mutacao,
  };
};

export const montarLinhasHeranca = (contexto: IContexto): ILinhaHeranca[] => {
  const { dna, item, expectativas, gruposRentabilidade } = contexto;
  const publicosDoDna = (dna.publicosAlvo ?? []).length;
  const publicosDoItem = (item.publicosAlvo ?? []).length;
  const publicosMutados = publicosDoItem > 0;

  return [
    {
      numero: 1,
      label: 'Públicos-Alvo',
      valor: contarPublicos(publicosMutados ? publicosDoItem : publicosDoDna),
      valorHerdado: publicosMutados ? contarPublicos(publicosDoDna) : null,
      projecao: publicosMutados
        ? projecaoMedida(contexto.populacaoDoItem)
        : projecaoPopulacao(expectativas),
      projecaoMedindo: publicosMutados && !!contexto.populacaoDoItem?.medindo,
      mutacao: publicosMutados,
    },
    linhaSimples(2, 'Tipo Estratégico', {
      doItem: item.tipoEstrategicoPLM,
      doDna: dna.tipoEstrategico,
      formatar: (valor) => TIPO_ESTRATEGICO_PLM_LABELS[valor] ?? SEM_VALOR,
    }),
    linhaSimples(3, 'Taxa de Adoção', {
      doItem: item.taxaAdocaoPLM,
      doDna: dna.taxaAdocao,
      formatar: formatarPercentual,
      projecao: projecaoClientesEsperados(expectativas),
    }),
    linhaSimples(4, 'Peso Médio de Maturação', {
      doItem: item.pesoMedioMaturacaoPLM,
      doDna: dna.pesoMedioMaturacao,
      formatar: (valor) => `${formatarNumero(valor, 6)} KG`,
    }),
    linhaCurva(contexto),
    linhaSimples(6, 'ICV esperado', {
      doItem: estaPreenchido(contexto.idGrupoRentabilidadeItem)
        ? contexto.idGrupoRentabilidadeItem
        : undefined,
      doDna: dna.idGrupoRentabilidade,
      formatar: (id) => nomeDoGrupo(gruposRentabilidade, id),
    }),
  ];
};
